import * as Xstate from "@xstate/react";
import React, { useEffect } from "react";
import {
  HiArrowsRightLeft,
  HiOutlineCog6Tooth,
  HiOutlineUserCircle,
} from "react-icons/hi2";
import { HiOutlineBars3, HiOutlineXMark } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

import * as devConfig from "../../app/configs/devConfig";
import { LABELS, PATHS, ROUTE_NAMES } from "../../app/constants/TextConstants";
import { getAuthHeader } from "../../app/globalState/GlobalAuthState";
import { GlobalContext } from "../../app/stateMachines/GlobalContext";
import { AvatarPlaceholder } from "../../components/avatar/AvatarPlaceholder";
import LegacyButton from "../../components/button/Button";
import { LogoLink } from "../../components/logos/LogoLink";
import { SignOutSvg } from "../../components/svg/SignOutSvg";
import { useUnsavedChangesGuard } from "../../components/UnsavedChangesGuard";
import { useAppDispatch, useAppSelector } from "../../redux/redux";
import { getUser, setIsLinkedFirm } from "../../services/AppService";
import { elapsedTimer } from "../../services/TimerService";
import {
  deleteLinkedFirm,
  getLinkedFirm,
} from "../../sessionStorage/sessionStorage";
import { NotificationBell } from "../notifications/NotificationBell";
import HeaderSearch from "./Search/HeaderSearch";
import Timer from "./Timer";

/**
 * The avatar menu is a daisyUI focus dropdown: it stays open while focus is
 * inside it, so picking an item (which navigates) left it open (QA D3).
 * Moving the focus away closes it.
 */
const closeAvatarMenu = () => {
  const active = document.activeElement;
  if (active instanceof HTMLElement) active.blur();
};

function clearAllCookies() {
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf("=");
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie =
      name + "=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;";
  }
}

type MainTopNavProps = {
  /** Phones only: the left menu overlay is open. */
  isMobileMenuOpen?: boolean;
  /** Phones only: the menu button (hidden from md up). */
  onToggleMobileMenu?: () => void;
};

export const MainTopNav: React.FC<MainTopNavProps> = ({
  isMobileMenuOpen = false,
  onToggleMobileMenu,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { runWithUnsavedGuard } = useUnsavedChangesGuard();

  const { user } = useAppSelector((state) => state.appReducer);
  const linkedFirm = getLinkedFirm();
  const orgId = linkedFirm?.orgId || "";

  const { userInfoService } = React.useContext(GlobalContext);
  const [userInfoState] = Xstate.useActor(userInfoService);
  const { userInfoByEmail } = userInfoState.context;

  const handleDeleteLinkFirm = async () => {
    const selectedFirm = getLinkedFirm();
    if (selectedFirm) {
      const response = await fetch(
        `${devConfig.getServerBaseUrlForEnv()}/api/user-organization/remove-link`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: getAuthHeader().Authorization,
          },
          method: "DELETE",
          body: JSON.stringify({ userId: user?.id, orgId: selectedFirm.id }),
        },
      );
      if (response.ok) {
        deleteLinkedFirm();
      } else {
      }
    }
  };

  useEffect(() => {
    dispatch(
      getUser({
        userId: userInfoByEmail?.id || "",
        orgId: getLinkedFirm()?.orgId,
      }),
    );
  }, []);

  useEffect(() => {
    if (user?.id && orgId) {
      dispatch(
        elapsedTimer({
          userId: user.id,
          orgId,
        }),
      );
    }
  }, [dispatch, orgId, user?.id]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && user?.id && orgId) {
        dispatch(
          elapsedTimer({
            userId: user.id,
            orgId,
          }),
        );
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, orgId, user?.id]);

  return (
    <>
      <div
        className={
          "z-header navbar fixed top-0 h-[64px] border border-gray-300 bg-white max-md:gap-1 max-md:px-2"
        }>
        {onToggleMobileMenu && (
          <button
            type={"button"}
            onClick={onToggleMobileMenu}
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            aria-expanded={isMobileMenuOpen}
            className={
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-[#1F2433] hover:bg-gray-100 md:!hidden"
            }>
            {isMobileMenuOpen ? (
              <HiOutlineXMark className={"h-6 w-6"} />
            ) : (
              <HiOutlineBars3 className={"h-6 w-6"} />
            )}
          </button>
        )}
        <div className={"border-base-200 bg-base-100 max-md:hidden"}>
          <LogoLink />
        </div>
        <div className={"flex min-w-0 flex-1 justify-center"}>
          <div
            className={
              "flex h-10 min-w-[300px] max-w-full basis-1/3 items-center rounded-lg border border-gray-400 max-md:min-w-0 max-md:basis-full"
            }>
            <HeaderSearch />
          </div>
        </div>
        <div className={"flex shrink-0 gap-2 max-md:gap-0"}>
          <NotificationBell />
          <Timer />
          <div className={"dropdown dropdown-end"}>
            <label tabIndex={0} className={"avatar btn btn-circle btn-ghost"}>
              <div className={"w-10 rounded-full"}>
                <AvatarPlaceholder
                  size={"extra-small"}
                  type={"custom"}
                  avatarCustomColor={user?.userProfile?.color}
                  label={
                    userInfoByEmail?.userProfile?.avatarInitials ??
                    LABELS.unknownUser.defaultInitials
                  }
                />
              </div>
            </label>
            <ul
              tabIndex={0}
              className={
                "dropdown-content menu rounded-box menu-compact mt-3 w-64 bg-base-100 p-2 shadow"
              }>
              <li>
                <div
                  onClick={() => {
                    closeAvatarMenu();
                    navigate(PATHS.user());
                  }}>
                  <div className={"flex items-center gap-2"}>
                    <div>
                      <HiOutlineUserCircle className={"h-4 w-4"} />
                    </div>
                    <div>Profile</div>
                  </div>
                </div>
              </li>
              {!user?.userRoleEnum?.userRole?.includes("Staff") && (
                <li>
                  <div
                    onClick={() => {
                      closeAvatarMenu();
                      navigate(PATHS.settings());
                    }}>
                    <div className={"flex items-center gap-2"}>
                      <div>
                        <HiOutlineCog6Tooth className={"h-4 w-4"} />
                      </div>
                      <div>Settings</div>
                    </div>
                  </div>
                </li>
              )}
              <li>
                <LegacyButton
                  colorType={"ghost"}
                  icon={<HiArrowsRightLeft className={"h-4 w-4"} />}
                  label={"Switch firm"}
                  onClick={() => {
                    closeAvatarMenu();
                    runWithUnsavedGuard(() =>
                      handleDeleteLinkFirm().then(() => {
                        deleteLinkedFirm();
                        dispatch(setIsLinkedFirm(false));
                      }),
                    );
                  }}
                  position={"start"}
                  size={"small"}
                  extraClasses={
                    "normal-case font-normal w-full py-2 h-fit min-h-fit"
                  }
                />
              </li>
              <li>
                <LegacyButton
                  colorType={"ghost"}
                  label={LABELS.features.signout}
                  icon={<SignOutSvg sizeCx={"h-4 w-4"} />}
                  onClick={() => {
                    handleDeleteLinkFirm().then(() => {
                      sessionStorage.clear();
                      clearAllCookies();
                      navigate(ROUTE_NAMES.signOut);
                      dispatch(setIsLinkedFirm(false));
                    });
                  }}
                  position={"start"}
                  size={"small"}
                  extraClasses={
                    "normal-case font-normal w-full py-2 h-fit min-h-fit"
                  }
                />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </>
  );
};
